import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, provideAppInitializer, inject, ErrorHandler } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';

import { routes } from './app.routes';
import { retryAfterInterceptor } from './_interceptors/retry-after.interceptor';
import { authInterceptor } from './_interceptors/auth.interceptor';
import { sentryInterceptor } from './_interceptors/sentry.interceptor';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { FeatureFlagsService } from './_services/feature-flags.service';
import { RegionService } from './_services/region.service';
import { AuthService } from './_services/auth.service';
import { AnalyticsService } from './_services/analytics.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([retryAfterInterceptor, authInterceptor, sentryInterceptor])),
    provideAnimations(),
    importProvidersFrom(MatNativeDateModule, MatSnackBarModule),
    importProvidersFrom(TranslateModule.forRoot({})),
    ...provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // Restore the persisted region BEFORE any other initialiser/HTTP call so
    // that downstream services read the correct regional URLs.
    provideAppInitializer(() => inject(RegionService).restore()),
    // Re-fetch the user on app load so Hoffweb config changes are reflected on
    // reload without re-login. Runs after region restore so it hits the correct
    // regional host; falls back to the cached user on failure.
    provideAppInitializer(() => inject(AuthService).syncUser()),
    provideAppInitializer(() => { inject(Sentry.TraceService); }),
    provideAppInitializer(() => inject(FeatureFlagsService).init()),
    // Wire session/navigation product-analytics to the app's existing signals.
    provideAppInitializer(() => inject(AnalyticsService).init()),
  ]
};
